import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

// Dark theme styles
const darkStyles = {
  container: {
    color: '#f1f5f9',
  },
  card: {
    background: '#1e293b',
    padding: '1.5rem',
    borderRadius: '12px',
    marginBottom: '1rem',
    border: '1px solid #334155',
  },
  innerCard: {
    background: '#334155',
    padding: '1rem',
    borderRadius: '8px',
    marginTop: '1rem',
    border: '1px dashed #475569',
  },
  holderCard: {
    background: '#1e293b',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    border: '1px solid #475569',
  },
  label: {
    color: '#cbd5e1',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '0.5rem',
    display: 'block',
  },
  input: {
    background: '#0f172a',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem',
    color: '#f1f5f9',
    width: '100%',
    fontSize: '0.875rem',
  },
  select: {
    background: '#0f172a',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem',
    color: '#f1f5f9',
    width: '100%',
    fontSize: '0.875rem',
  },
  heading: {
    color: '#ffffff',
    marginBottom: '1rem',
    fontSize: '1rem',
    fontWeight: '600',
  },
  subHeading: {
    color: '#f59e0b',
    marginBottom: '0.75rem',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    padding: '0.5rem',
  },
  addBtn: {
    background: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    color: '#f1f5f9',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    marginTop: '1rem',
  },
  navBtn: {
    background: '#f59e0b',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    color: '#0f172a',
    cursor: 'pointer',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  navBtnDisabled: {
    background: '#475569',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    color: '#94a3b8',
    cursor: 'not-allowed',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  categoryNav: {
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    padding: '0.75rem',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
  },
};

const Step4Assets = ({ data, onNext, onPrevious, onSaveAndExit, onUpdate, saving }) => {
  const [formData, setFormData] = useState({
    immovable_assets: data.immovable_assets || [],
    financial_assets: data.financial_assets || {
      bank_accounts: [],
      insurance: [],
      mutual_funds: [],
      shares: [],
      pf_pension: []
    },
    tangible_assets: data.tangible_assets || [],
    intellectual_property: data.intellectual_property || [],
    safety_lockers: data.safety_lockers || [],
    business_interests: data.business_interests || [],
    esops: data.esops || [],
    digital_assets: data.digital_assets || [],
    foreign_assets: data.foreign_assets || [],
    // Mutual Fund holding declaration clauses (optional)
    mf_clauses: data.mf_clauses || {
      sole_holder: false,
      primary_holder: false,
      secondary_holder: false
    }
  });

  // Relationship options for joint holders
  const relationshipOptions = [
    'Spouse',
    'Son',
    'Daughter',
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandson',
    'Granddaughter',
    'Nephew',
    'Niece',
    'Uncle',
    'Aunt',
    'Cousin',
    'Partner',
    'Friend',
    'Other'
  ];

  // Collect all names from previous steps for autocomplete
  const getAllNames = () => {
    const names = [];
    
    // From Step 1 - Personal info
    if (data.testator_name) names.push(data.testator_name);
    if (data.father_name) names.push(data.father_name);
    if (data.mother_name) names.push(data.mother_name);
    
    // From Step 2 - Family members
    if (data.spouse_name) names.push(data.spouse_name);
    if (data.children && Array.isArray(data.children)) {
      data.children.forEach(child => {
        if (child.name) names.push(child.name);
      });
    }
    if (data.family_members && Array.isArray(data.family_members)) {
      data.family_members.forEach(member => {
        if (member.name) names.push(member.name);
      });
    }
    
    // From Step 3 - Executors and witnesses
    if (data.primary_executor?.name) names.push(data.primary_executor.name);
    if (data.alternate_executor?.name) names.push(data.alternate_executor.name);
    if (data.witness1?.name) names.push(data.witness1.name);
    if (data.witness2?.name) names.push(data.witness2.name);
    if (data.guardian_name) names.push(data.guardian_name);
    
    // Remove duplicates and empty values
    return [...new Set(names.filter(name => name && name.trim()))];
  };

  const allNames = getAllNames();

  // Define fixed category order (same as Step 3)
  const fixedCategoryOrder = [
    'immovable',
    'bank_accounts',
    'insurance',
    'mutual_funds',
    'shares',
    'pf_pension',
    'business',
    'esop_rsu',
    'digital_assets',
    'vehicles',
    'jewellery',
    'intellectual_property',
    'locker',
    'foreign_assets'
  ];

  // Sort selected categories according to fixed order
  const selectedCategories = (data.selected_asset_categories || []).sort((a, b) => {
    return fixedCategoryOrder.indexOf(a) - fixedCategoryOrder.indexOf(b);
  });
  
  // Track current category being viewed
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  
  // Track current asset index for each category
  const [currentAssetIndex, setCurrentAssetIndex] = useState({
    immovable_assets: 0,
    bank_accounts: 0,
    insurance: 0,
    mutual_funds: 0,
    shares: 0,
    pf_pension: 0,
    tangible_assets: 0,
    intellectual_property: 0,
    safety_lockers: 0,
    business_interests: 0,
    esops: 0,
    digital_assets: 0,
    foreign_assets: 0
  });

  // Map category codes to their display info (using codes from Step4AssetCategories)
  const categoryMap = {
    'immovable': { key: 'immovable_assets', title: 'Immovable Property' },
    'bank_accounts': { key: 'bank_accounts', title: 'Bank Accounts', isFinancial: true },
    'insurance': { key: 'insurance', title: 'Insurance Policies', isFinancial: true },
    'mutual_funds': { key: 'mutual_funds', title: 'Mutual Funds', isFinancial: true },
    'shares': { key: 'shares', title: 'Shares & Securities', isFinancial: true },
    'pf_pension': { key: 'pf_pension', title: 'PF & Pension', isFinancial: true },
    'vehicles': { key: 'tangible_assets', title: 'Vehicles', filter: 'vehicle' },
    'jewellery': { key: 'tangible_assets', title: 'Jewellery & Valuables' },
    'locker': { key: 'safety_lockers', title: 'Safety Lockers' },
    'business': { key: 'business_interests', title: 'Business Interests' },
    'esop_rsu': { key: 'esops', title: 'ESOPs / RSUs' },
    'digital_assets': { key: 'digital_assets', title: 'Digital Assets' },
    'intellectual_property': { key: 'intellectual_property', title: 'Intellectual Property' },
    'foreign_assets': { key: 'foreign_assets', title: 'Foreign Assets' }
  };

  // Get current category details
  const currentCategory = selectedCategories[currentCategoryIndex];
  const currentCategoryInfo = categoryMap[currentCategory];

  // Scroll to top when component mounts or category changes
  useEffect(() => {
    const scrollContainer = document.getElementById('will-scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [currentCategoryIndex]);

  // Auto-add first asset when category has 0 assets
  // Category navigation
  const navigateCategory = async (direction) => {
    // Save current data before navigating to another category
    if (onUpdate) {
      await onUpdate(formData);
    }
    
    setCurrentCategoryIndex(prev => prev + direction);
    // Scroll to top - check for dashboard container
    const scrollContainer = document.getElementById('will-scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  };

  // Navigation helper functions
  const navigateAsset = (category, direction) => {
    setCurrentAssetIndex(prev => {
      const newIndex = prev[category] + direction;
      return { ...prev, [category]: newIndex };
    });
  };

  const getAssetCount = (category) => {
    if (category.includes('_')) {
      // Financial assets sub-category
      const subCat = category;
      return formData.financial_assets[subCat]?.length || 0;
    }
    return formData[category]?.length || 0;
  };

  // Category Navigation Component
  const CategoryNavigation = () => {
    return (
      <div style={{ 
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: '#1e293b'
        }}>
          <button
            type="button"
            onClick={() => navigateCategory(-1)}
            disabled={currentCategoryIndex === 0}
            style={{
              padding: '0.5rem 0.75rem',
              background: currentCategoryIndex === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.95)',
              color: currentCategoryIndex === 0 ? 'rgba(255,255,255,0.5)' : '#059669',
              border: 'none',
              borderRadius: '8px',
              cursor: currentCategoryIndex === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <ChevronLeft size={16} /> <span className="hidden sm:inline">Previous</span>
          </button>
          
          <div style={{ textAlign: 'center', flex: 1, minWidth: 0, padding: '0 0.5rem' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>
              Category {currentCategoryIndex + 1} of {selectedCategories.length}
            </div>
            <div style={{ 
              fontSize: '1rem', 
              fontWeight: 800,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentCategoryInfo?.title}
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => navigateCategory(1)}
            disabled={currentCategoryIndex >= selectedCategories.length - 1}
            style={{
              padding: '0.5rem 0.75rem',
              background: currentCategoryIndex >= selectedCategories.length - 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.95)',
              color: currentCategoryIndex >= selectedCategories.length - 1 ? 'rgba(255,255,255,0.5)' : '#059669',
              border: 'none',
              borderRadius: '8px',
              cursor: currentCategoryIndex >= selectedCategories.length - 1 ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <span className="hidden sm:inline">Next</span> <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  // Asset Navigation Component
  const AssetNavigation = ({ category, label }) => {
    const count = getAssetCount(category);
    const current = currentAssetIndex[category];
    
    // Only show if there are multiple assets
    if (count <= 1) return null;
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '0.75rem', 
        background: '#f59e0b',
        borderRadius: '8px',
        marginBottom: '1rem',
        color: '#1e293b',
        gap: '0.5rem'
      }}>
        <button
          type="button"
          onClick={() => navigateAsset(category, -1)}
          disabled={current === 0}
          style={{
            padding: '0.4rem 0.75rem',
            background: current === 0 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.9)',
            color: current === 0 ? 'rgba(255,255,255,0.5)' : '#f59e0b',
            border: 'none',
            borderRadius: '6px',
            cursor: current === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.75rem',
            flexShrink: 0
          }}
        >
          <ChevronLeft size={14} /> <span className="hidden sm:inline">Prev</span>
        </button>
        
        <div style={{ 
          fontSize: '0.875rem', 
          fontWeight: 700,
          textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          textAlign: 'center',
          flex: 1,
          minWidth: 0
        }}>
          <span className="hidden sm:inline">{label} </span>{current + 1} of {count}
        </div>
        
        <button
          type="button"
          onClick={() => navigateAsset(category, 1)}
          disabled={current >= count - 1}
          style={{
            padding: '0.4rem 0.75rem',
            background: current >= count - 1 ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.9)',
            color: current >= count - 1 ? 'rgba(255,255,255,0.5)' : '#f59e0b',
            border: 'none',
            borderRadius: '6px',
            cursor: current >= count - 1 ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.75rem',
            flexShrink: 0
          }}
        >
          <span className="hidden sm:inline">Next</span> <ChevronRight size={14} />
        </button>
      </div>
    );
  };

  // Helper functions for joint holders
  const getHolderLabel = (index) => {
    const labels = ['Second Holder', 'Third Holder', 'Fourth Holder', 'Fifth Holder', 'Sixth Holder'];
    return labels[index] || `Holder ${index + 2}`;
  };

  const addJointHolder = (category, assetIndex, subCategory = null) => {
    const newJointHolder = { name: '', relationship: '', share_percentage: '' };
    
    if (category === 'immovable_assets') {
      setFormData(prev => ({
        ...prev,
        immovable_assets: prev.immovable_assets.map((asset, i) =>
          i === assetIndex ? { ...asset, joint_holders: [...(asset.joint_holders || []), newJointHolder] } : asset
        )
      }));
    } else if (category === 'financial_assets' && subCategory) {
      setFormData(prev => ({
        ...prev,
        financial_assets: {
          ...prev.financial_assets,
          [subCategory]: prev.financial_assets[subCategory].map((item, i) =>
            i === assetIndex ? { ...item, joint_holders: [...(item.joint_holders || []), newJointHolder] } : item
          )
        }
      }));
    } else if (category === 'business_interests') {
      setFormData(prev => ({
        ...prev,
        business_interests: prev.business_interests.map((item, i) =>
          i === assetIndex ? { ...item, joint_holders: [...(item.joint_holders || []), newJointHolder] } : item
        )
      }));
    }
  };

  const updateJointHolder = (category, assetIndex, holderIndex, field, value, subCategory = null) => {
    if (category === 'immovable_assets') {
      setFormData(prev => ({
        ...prev,
        immovable_assets: prev.immovable_assets.map((asset, i) =>
          i === assetIndex ? {
            ...asset,
            joint_holders: asset.joint_holders.map((holder, j) =>
              j === holderIndex ? { ...holder, [field]: value } : holder
            )
          } : asset
        )
      }));
    } else if (category === 'financial_assets' && subCategory) {
      setFormData(prev => ({
        ...prev,
        financial_assets: {
          ...prev.financial_assets,
          [subCategory]: prev.financial_assets[subCategory].map((item, i) =>
            i === assetIndex ? {
              ...item,
              joint_holders: (item.joint_holders || []).map((holder, j) =>
                j === holderIndex ? { ...holder, [field]: value } : holder
              )
            } : item
          )
        }
      }));
    } else if (category === 'business_interests') {
      setFormData(prev => ({
        ...prev,
        business_interests: prev.business_interests.map((item, i) =>
          i === assetIndex ? {
            ...item,
            joint_holders: (item.joint_holders || []).map((holder, j) =>
              j === holderIndex ? { ...holder, [field]: value } : holder
            )
          } : item
        )
      }));
    }
  };

  const removeJointHolder = (category, assetIndex, holderIndex, subCategory = null) => {
    if (category === 'immovable_assets') {
      setFormData(prev => ({
        ...prev,
        immovable_assets: prev.immovable_assets.map((asset, i) =>
          i === assetIndex ? { ...asset, joint_holders: asset.joint_holders.filter((_, j) => j !== holderIndex) } : asset
        )
      }));
    } else if (category === 'financial_assets' && subCategory) {
      setFormData(prev => ({
        ...prev,
        financial_assets: {
          ...prev.financial_assets,
          [subCategory]: prev.financial_assets[subCategory].map((item, i) =>
            i === assetIndex ? { ...item, joint_holders: (item.joint_holders || []).filter((_, j) => j !== holderIndex) } : item
          )
        }
      }));
    } else if (category === 'business_interests') {
      setFormData(prev => ({
        ...prev,
        business_interests: prev.business_interests.map((item, i) =>
          i === assetIndex ? { ...item, joint_holders: (item.joint_holders || []).filter((_, j) => j !== holderIndex) } : item
        )
      }));
    }
  };

  const addImmovableAsset = () => {
    setFormData(prev => ({
      ...prev,
      immovable_assets: [...prev.immovable_assets, {
        type: '',
        address: '',
        survey_no: '',
        share_percentage: 100,
        ownership: 'single',
        has_joint_holders: false,
        joint_holders: []
      }]
    }));
  };

  const updateImmovableAsset = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      immovable_assets: prev.immovable_assets.map((asset, i) => 
        i === index ? { ...asset, [field]: value } : asset
      )
    }));
  };

  const removeImmovableAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      immovable_assets: prev.immovable_assets.filter((_, i) => i !== index)
    }));
  };

  const addBankAccount = () => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        bank_accounts: [...prev.financial_assets.bank_accounts, {
          bank_name: '',
          branch: '',
          account_number: '',
          account_type: '',
          has_joint_holders: false,
          joint_holders: []
        }]
      }
    }));
  };

  const updateBankAccount = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        bank_accounts: prev.financial_assets.bank_accounts.map((acc, i) => 
          i === index ? { ...acc, [field]: value } : acc
        )
      }
    }));
  };

  // Special handler for toggling joint holders - auto-adds first holder when enabled
  const toggleBankJointHolders = (index, enabled) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        bank_accounts: prev.financial_assets.bank_accounts.map((acc, i) => {
          if (i === index) {
            if (enabled && (!acc.joint_holders || acc.joint_holders.length === 0)) {
              // Auto-add first joint holder when enabling
              return { 
                ...acc, 
                has_joint_holders: true, 
                joint_holders: [{ name: '', relationship: '', share_percentage: '' }] 
              };
            } else if (!enabled) {
              // Clear joint holders when disabling
              return { ...acc, has_joint_holders: false, joint_holders: [] };
            }
            return { ...acc, has_joint_holders: enabled };
          }
          return acc;
        })
      }
    }));
  };

  const removeBankAccount = (index) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        bank_accounts: prev.financial_assets.bank_accounts.filter((_, i) => i !== index)
      }
    }));
  };

  const addVehicle = () => {
    setFormData(prev => ({
      ...prev,
      tangible_assets: [...prev.tangible_assets, {
        type: 'vehicle',
        description: '',
        registration_no: '',
        year: ''
      }]
    }));
  };

  const updateVehicle = (filteredIndex, field, value) => {
    setFormData(prev => {
      // Find the actual index in tangible_assets array
      const vehicles = prev.tangible_assets.filter(a => a.type === 'vehicle');
      const vehicleToUpdate = vehicles[filteredIndex];
      const actualIndex = prev.tangible_assets.findIndex(a => a === vehicleToUpdate);
      
      return {
        ...prev,
        tangible_assets: prev.tangible_assets.map((asset, i) => 
          i === actualIndex ? { ...asset, [field]: value } : asset
        )
      };
    });
  };

  const removeVehicle = (filteredIndex) => {
    setFormData(prev => {
      // Find the actual index in tangible_assets array
      const vehicles = prev.tangible_assets.filter(a => a.type === 'vehicle');
      const vehicleToRemove = vehicles[filteredIndex];
      const actualIndex = prev.tangible_assets.findIndex(a => a === vehicleToRemove);
      
      return {
        ...prev,
        tangible_assets: prev.tangible_assets.filter((_, i) => i !== actualIndex)
      };
    });
  };

  // Insurance handlers
  const addInsurance = () => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        insurance: [...prev.financial_assets.insurance, {
          company_name: '',
          policy_type: '',
          policy_number: '',
          sum_assured: ''
        }]
      }
    }));
  };

  const updateInsurance = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        insurance: prev.financial_assets.insurance.map((ins, i) => 
          i === index ? { ...ins, [field]: value } : ins
        )
      }
    }));
  };

  const removeInsurance = (index) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        insurance: prev.financial_assets.insurance.filter((_, i) => i !== index)
      }
    }));
  };

  // Handler to update MF clauses (Mutual funds now use declaration clauses only)
  const updateMFClause = (clauseKey, value) => {
    setFormData(prev => ({
      ...prev,
      mf_clauses: {
        ...prev.mf_clauses,
        [clauseKey]: value
      }
    }));
  };

  // Shares handlers
  const addShare = () => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        shares: [...prev.financial_assets.shares, {
          input_method: 'manual',
          company_name: '',
          quantity: '',
          demat_account: '',
          dp_id: '',
          statement_file: null,
          has_joint_holders: false,
          joint_holders: []
        }]
      }
    }));
  };

  const handleShareStatementUpload = async (index, file) => {
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      toast.error('Please upload a PDF or image file.');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB.');
      return;
    }
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const newFormData = {
        ...formData,
        financial_assets: {
          ...formData.financial_assets,
          shares: formData.financial_assets.shares.map((share, i) => 
            i === index ? { ...share, statement_file: {
              name: file.name,
              data: reader.result,
              type: file.type
            }} : share
          )
        }
      };
      setFormData(newFormData);
      
      // Auto-save after upload
      if (onUpdate) {
        await onUpdate(newFormData);
      }
      toast.success('Statement uploaded and saved');
    };
    reader.onerror = () => {
      toast.error('Failed to read file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const updateShare = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        shares: prev.financial_assets.shares.map((share, i) => 
          i === index ? { ...share, [field]: value } : share
        )
      }
    }));
  };

  // Special handler for toggling joint holders in shares
  const toggleShareJointHolders = (index, enabled) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        shares: prev.financial_assets.shares.map((share, i) => {
          if (i === index) {
            if (enabled && (!share.joint_holders || share.joint_holders.length === 0)) {
              return { 
                ...share, 
                has_joint_holders: true, 
                joint_holders: [{ name: '', relationship: '', share_percentage: '' }] 
              };
            } else if (!enabled) {
              return { ...share, has_joint_holders: false, joint_holders: [] };
            }
            return { ...share, has_joint_holders: enabled };
          }
          return share;
        })
      }
    }));
  };

  const removeShare = (index) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        shares: prev.financial_assets.shares.filter((_, i) => i !== index)
      }
    }));
  };

  // PF/Pension handlers
  const addPFPension = () => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        pf_pension: [...prev.financial_assets.pf_pension, {
          type: '',
          account_number: '',
          organization: ''
        }]
      }
    }));
  };

  const updatePFPension = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        pf_pension: prev.financial_assets.pf_pension.map((pf, i) => 
          i === index ? { ...pf, [field]: value } : pf
        )
      }
    }));
  };

  const removePFPension = (index) => {
    setFormData(prev => ({
      ...prev,
      financial_assets: {
        ...prev.financial_assets,
        pf_pension: prev.financial_assets.pf_pension.filter((_, i) => i !== index)
      }
    }));
  };

  // Valuable/Jewellery handlers
  const addValuable = () => {
    setFormData(prev => ({
      ...prev,
      tangible_assets: [...prev.tangible_assets, {
        type: 'jewellery',
        description: '',
        value: '',
        additional_notes: ''
      }]
    }));
  };

  const updateValuable = (filteredIndex, field, value) => {
    setFormData(prev => {
      // Find the actual index in tangible_assets array
      const valuables = prev.tangible_assets.filter(a => a.type === 'jewellery' || a.type === 'valuable' || a.type === 'gold' || a.type === 'silver' || a.type === 'artwork');
      const valuableToUpdate = valuables[filteredIndex];
      const actualIndex = prev.tangible_assets.findIndex(a => a === valuableToUpdate);
      
      return {
        ...prev,
        tangible_assets: prev.tangible_assets.map((asset, i) => 
          i === actualIndex ? { ...asset, [field]: value } : asset
        )
      };
    });
  };

  const removeValuable = (filteredIndex) => {
    setFormData(prev => {
      // Find the actual index in tangible_assets array
      const valuables = prev.tangible_assets.filter(a => a.type === 'jewellery' || a.type === 'valuable' || a.type === 'gold' || a.type === 'silver' || a.type === 'artwork');
      const valuableToRemove = valuables[filteredIndex];
      const actualIndex = prev.tangible_assets.findIndex(a => a === valuableToRemove);
      
      return {
        ...prev,
        tangible_assets: prev.tangible_assets.filter((_, i) => i !== actualIndex)
      };
    });
  };

  // Safety Locker handlers
  const addLocker = () => {
    setFormData(prev => ({
      ...prev,
      safety_lockers: [...prev.safety_lockers, {
        bank_name: '',
        branch: '',
        locker_number: '',
        key_location: ''
      }]
    }));
  };

  const updateLocker = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      safety_lockers: prev.safety_lockers.map((locker, i) => 
        i === index ? { ...locker, [field]: value } : locker
      )
    }));
  };

  const removeLocker = (index) => {
    setFormData(prev => ({
      ...prev,
      safety_lockers: prev.safety_lockers.filter((_, i) => i !== index)
    }));
  };

  // Business Interests handlers
  const addBusiness = () => {
    setFormData(prev => ({
      ...prev,
      business_interests: [...prev.business_interests, {
        business_type: '',
        other_type: '',
        business_name: '',
        shares_held: '',
        ownership_percentage: '',
        has_joint_holders: false,
        joint_holders: []
      }]
    }));
  };

  const updateBusiness = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      business_interests: prev.business_interests.map((business, i) => 
        i === index ? { ...business, [field]: value } : business
      )
    }));
  };

  const removeBusiness = (index) => {
    setFormData(prev => ({
      ...prev,
      business_interests: prev.business_interests.filter((_, i) => i !== index)
    }));
  };

  // ESOP/RSU handlers
  const addEsop = () => {
    setFormData(prev => ({
      ...prev,
      esops: [...prev.esops, {
        firm_name: '',
        total_shares: '',
        vested_esops: '',
        unvested_esops: ''
      }]
    }));
  };

  const updateEsop = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      esops: prev.esops.map((esop, i) => 
        i === index ? { ...esop, [field]: value } : esop
      )
    }));
  };

  const removeEsop = (index) => {
    setFormData(prev => ({
      ...prev,
      esops: prev.esops.filter((_, i) => i !== index)
    }));
  };

  // Digital Assets handlers
  const addDigitalAsset = () => {
    setFormData(prev => ({
      ...prev,
      digital_assets: [...prev.digital_assets, {
        platform_name: '',
        wallet_address: '',
        description: ''
      }]
    }));
  };

  const updateDigitalAsset = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      digital_assets: prev.digital_assets.map((asset, i) => 
        i === index ? { ...asset, [field]: value } : asset
      )
    }));
  };

  const removeDigitalAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      digital_assets: prev.digital_assets.filter((_, i) => i !== index)
    }));
  };

  // Intellectual Property handlers
  const addIP = () => {
    setFormData(prev => ({
      ...prev,
      intellectual_property: [...prev.intellectual_property, {
        name: '',
        unique_identifier: '',
        location: '',
        patent_no: ''
      }]
    }));
  };

  const updateIP = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      intellectual_property: prev.intellectual_property.map((ip, i) => 
        i === index ? { ...ip, [field]: value } : ip
      )
    }));
  };

  const removeIP = (index) => {
    setFormData(prev => ({
      ...prev,
      intellectual_property: prev.intellectual_property.filter((_, i) => i !== index)
    }));
  };

  // Foreign Assets handlers
  const addForeignAsset = () => {
    setFormData(prev => ({
      ...prev,
      foreign_assets: [...prev.foreign_assets, {
        country: '',
        institution: '',
        account_number: '',
        asset_type: '',
        description: ''
      }]
    }));
  };

  const updateForeignAsset = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      foreign_assets: prev.foreign_assets.map((asset, i) => 
        i === index ? { ...asset, [field]: value } : asset
      )
    }));
  };

  const removeForeignAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      foreign_assets: prev.foreign_assets.filter((_, i) => i !== index)
    }));
  };

  // Auto-add first asset when category has 0 assets
  useEffect(() => {
    if (!currentCategory || !currentCategoryInfo) return;
    
    // Mutual funds uses declaration clauses only - no auto-add needed
    if (currentCategory === 'mutual_funds') return;
    
    const categoryKey = currentCategoryInfo.key;
    const isFinancial = currentCategoryInfo.isFinancial;
    
    // Check if category has 0 assets
    let hasAssets = false;
    if (isFinancial) {
      hasAssets = formData.financial_assets[categoryKey]?.length > 0;
    } else if (currentCategory === 'vehicles') {
      hasAssets = formData.tangible_assets?.filter(a => a.type === 'vehicle').length > 0;
    } else if (currentCategory === 'jewellery') {
      hasAssets = formData.tangible_assets?.filter(a => 
        a.type === 'jewellery' || a.type === 'valuable' || a.type === 'gold' || 
        a.type === 'silver' || a.type === 'artwork'
      ).length > 0;
    } else {
      hasAssets = formData[categoryKey]?.length > 0;
    }
    
    // If no assets, automatically add the first one
    if (!hasAssets) {
      switch(currentCategory) {
        case 'immovable': addImmovableAsset(); break;
        case 'bank_accounts': addBankAccount(); break;
        case 'insurance': addInsurance(); break;
        case 'mutual_funds': break; // MF uses declaration clauses only, no individual entries needed
        case 'shares': addShare(); break;
        case 'pf_pension': addPFPension(); break;
        case 'vehicles': addVehicle(); break;
        case 'jewellery': addValuable(); break;
        case 'locker': addLocker(); break;
        case 'business': addBusiness(); break;
        case 'esop_rsu': addEsop(); break;
        case 'digital_assets': addDigitalAsset(); break;
        case 'intellectual_property': addIP(); break;
        case 'foreign_assets': addForeignAsset(); break;
        default: break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCategory]);

  const validateSharePercentages = () => {
    const errors = [];

    // Validate immovable assets
    formData.immovable_assets.forEach((asset, assetIdx) => {
      if (asset.ownership === 'joint' && asset.joint_holders && asset.joint_holders.length > 0) {
        // Include primary holder's share + all joint holders' shares
        const primaryShare = parseFloat(asset.share_percentage) || 0;
        const jointHoldersTotal = asset.joint_holders.reduce((sum, holder) => 
          sum + (parseFloat(holder.share_percentage) || 0), 0
        );
        const totalShare = primaryShare + jointHoldersTotal;
        if (totalShare > 0 && Math.abs(totalShare - 100) > 0.01) {
          errors.push(`Immovable Property ${assetIdx + 1}: All holder shares (including yours) must total 100% (current: ${totalShare.toFixed(2)}%)`);
        }
        // Validate joint holder names and relationships
        asset.joint_holders.forEach((holder, holderIdx) => {
          if (!holder.name || !holder.name.trim()) {
            errors.push(`Immovable Property ${assetIdx + 1}, Joint Holder ${holderIdx + 1}: Name is required`);
          }
          if (!holder.relationship) {
            errors.push(`Immovable Property ${assetIdx + 1}, Joint Holder ${holderIdx + 1}: Relationship is required`);
          }
        });
      }
    });

    // Validate bank accounts joint holders
    formData.financial_assets.bank_accounts.forEach((account, accIdx) => {
      if (account.has_joint_holders && account.joint_holders && account.joint_holders.length > 0) {
        account.joint_holders.forEach((holder, holderIdx) => {
          if (!holder.name || !holder.name.trim()) {
            errors.push(`Bank Account ${accIdx + 1}, Joint Holder ${holderIdx + 1}: Name is required`);
          }
          if (!holder.relationship) {
            errors.push(`Bank Account ${accIdx + 1}, Joint Holder ${holderIdx + 1}: Relationship is required`);
          }
        });
      }
    });

    // Note: Mutual funds validation removed - now uses holding declaration clauses only

    // Validate shares joint holders
    formData.financial_assets.shares.forEach((share, shareIdx) => {
      if (share.has_joint_holders && share.joint_holders && share.joint_holders.length > 0) {
        share.joint_holders.forEach((holder, holderIdx) => {
          if (!holder.name || !holder.name.trim()) {
            errors.push(`Shares ${shareIdx + 1}, Joint Holder ${holderIdx + 1}: Name is required`);
          }
          if (!holder.relationship) {
            errors.push(`Shares ${shareIdx + 1}, Joint Holder ${holderIdx + 1}: Relationship is required`);
          }
        });
      }
    });

    // Note: Joint holder share % validation removed for mutual_funds, shares, insurance, and pf_pension
    // as these categories don't require share percentage for joint holders

    // Validate business interests
    formData.business_interests.forEach((business, idx) => {
      if (business.joint_holders && business.joint_holders.length > 0) {
        const totalShare = business.joint_holders.reduce((sum, holder) => 
          sum + (parseFloat(holder.share_percentage) || 0), 0
        );
        if (totalShare > 0 && Math.abs(totalShare - 100) > 0.01) {
          errors.push(`Business Interest ${idx + 1}: Joint holder shares must total 100% (current: ${totalShare.toFixed(2)}%)`);
        }
        // Validate joint holder names and relationships
        business.joint_holders.forEach((holder, holderIdx) => {
          if (!holder.name || !holder.name.trim()) {
            errors.push(`Business Interest ${idx + 1}, Joint Holder ${holderIdx + 1}: Name is required`);
          }
          if (!holder.relationship) {
            errors.push(`Business Interest ${idx + 1}, Joint Holder ${holderIdx + 1}: Relationship is required`);
          }
        });
      }

      // Validate firm ownership percentage
      const firmPercentage = parseFloat(business.ownership_percentage) || 0;
      if (firmPercentage > 100) {
        errors.push(`Business Interest ${idx + 1}: Firm ownership cannot exceed 100% (current: ${firmPercentage}%)`);
      }
    });

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate share percentages and joint holder info
    const errors = validateSharePercentages();
    if (errors.length > 0) {
      toast.error('Please fix the following errors:\n\n' + errors.join('\n'));
      return;
    }
    
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} style={{ color: '#f1f5f9' }}>
      <style>{`
        .form-section h3 {
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #334155;
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        @media (min-width: 640px) {
          .form-row {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }
        }
        .form-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 1rem;
        }
        .form-group label {
          color: #cbd5e1;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          display: block;
        }
        .form-group input,
        .form-group select,
        .form-group textarea,
        input[type="text"],
        input[type="number"],
        input[type="email"],
        input[type="tel"],
        input[type="date"],
        select,
        textarea {
          background: #0f172a !important;
          border: 1px solid #475569 !important;
          border-radius: 8px !important;
          padding: 0.625rem 0.75rem !important;
          color: #f1f5f9 !important;
          font-size: 0.875rem !important;
          width: 100%;
          box-sizing: border-box;
          line-height: 1.5;
          text-align: left;
          vertical-align: middle;
        }
        @media (min-width: 640px) {
          .form-group input,
          .form-group select,
          .form-group textarea,
          input[type="text"],
          input[type="number"],
          input[type="email"],
          input[type="tel"],
          input[type="date"],
          select,
          textarea {
            padding: 0.75rem 1rem !important;
            font-size: 0.9rem !important;
          }
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus,
        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #f59e0b !important;
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
        }
        .form-group input::placeholder,
        .form-group textarea::placeholder,
        input::placeholder,
        textarea::placeholder {
          color: #64748b !important;
        }
        .form-group select option,
        select option {
          background: #1e293b;
          color: #f1f5f9;
          padding: 0.5rem;
        }
        .step-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #334155;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .btn-secondary {
          background: #334155;
          border: 1px solid #475569;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: #f1f5f9;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-secondary:hover {
          background: #475569;
        }
        .btn-outline {
          background: transparent;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: #f59e0b;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-outline:hover {
          background: rgba(245, 158, 11, 0.1);
        }
        .btn-primary {
          background: #f59e0b;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: #0f172a;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-primary:hover {
          background: #fbbf24;
        }
        .btn-primary:disabled {
          background: #475569;
          color: #94a3b8;
          cursor: not-allowed;
        }
      `}</style>
      {/* Category Navigation */}
      <CategoryNavigation />
      
      {/* Immovable Property */}
      {currentCategory === 'immovable' && (
        <div className="form-section">
          {formData.immovable_assets.map((asset, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Property Type *</label>
                  <select value={asset.type} onChange={(e) => updateImmovableAsset(index, 'type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Residential Flat">Residential Flat</option>
                    <option value="House">House</option>
                    <option value="Land">Land</option>
                    <option value="Commercial Property">Commercial Property</option>
                    <option value="Shop">Shop</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Ownership *</label>
                  <select value={asset.ownership} onChange={(e) => updateImmovableAsset(index, 'ownership', e.target.value)} required>
                    <option value="single">Single</option>
                    <option value="joint">Joint</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Your Share % *</label>
                  <input type="number" value={asset.share_percentage} onChange={(e) => updateImmovableAsset(index, 'share_percentage', e.target.value)} max="100" step="0.01" required />
                </div>
              </div>

              {/* Joint Holders Section - Show if ownership is joint */}
              {asset.ownership === 'joint' && (
                <div style={{ background: '#334155', padding: '1rem', borderRadius: '6px', marginTop: '1rem', border: '1px dashed #475569' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600 }}>Additional Holders</h4>
                  {(asset.joint_holders || []).map((holder, holderIndex) => (
                    <div key={holderIndex} style={{ background: '#1e293b', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
                      <h5 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#f1f5f9' }}>
                        {getHolderLabel(holderIndex)}
                      </h5>                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input 
                            type="text" 
                            value={holder.name || ''} 
                            onChange={(e) => updateJointHolder('immovable_assets', index, holderIndex, 'name', e.target.value)}
                            list="names-list"
                            placeholder="Start typing to see suggestions..."
                            required
                          />
                          <datalist id="names-list">
                            {allNames.map((name, i) => (
                              <option key={i} value={name} />
                            ))}
                          </datalist>
                        </div>
                        <div className="form-group">
                          <label>Relationship *</label>
                          <select 
                            value={holder.relationship || ''} 
                            onChange={(e) => updateJointHolder('immovable_assets', index, holderIndex, 'relationship', e.target.value)}
                            required
                          >
                            <option value="">Select Relationship</option>
                            {relationshipOptions.map(rel => (
                              <option key={rel} value={rel}>{rel}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Share % (Optional)</label>
                          <input 
                            type="number" 
                            value={holder.share_percentage || ''} 
                            onChange={(e) => updateJointHolder('immovable_assets', index, holderIndex, 'share_percentage', e.target.value)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button 
                            type="button" 
                            onClick={() => removeJointHolder('immovable_assets', index, holderIndex)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                          >
                            <Trash2 size={16} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => addJointHolder('immovable_assets', index)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#334155', border: '1px solid #475569', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', color: '#f1f5f9' }}
                  >
                    <Plus size={16} /> Add {(asset.joint_holders || []).length === 0 ? 'Second' : 'Another'} Holder
                  </button>
                </div>
              )}

              <div className="form-group">
                <label>Address *</label>
                <textarea value={asset.address} onChange={(e) => updateImmovableAsset(index, 'address', e.target.value)} rows="2" required></textarea>
              </div>
              <div className="form-group">
                <label>{asset.type === 'Residential Flat' ? 'Certificate Number' : 'Survey Number / Plot No'}</label>
                <input 
                  type="text" 
                  value={asset.survey_no} 
                  onChange={(e) => updateImmovableAsset(index, 'survey_no', e.target.value)} 
                  placeholder={asset.type === 'Residential Flat' ? 'Enter certificate number' : 'Enter survey/plot number'}
                />
              </div>
              <button type="button" onClick={() => removeImmovableAsset(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Property
              </button>
            </div>
          ))}
          <button type="button" onClick={addImmovableAsset} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Property
          </button>
        </div>
      )}

      {/* Bank Accounts */}
      {currentCategory === 'bank_accounts' && (
        <div className="form-section">
          {formData.financial_assets.bank_accounts.map((account, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name *</label>
                  <input type="text" value={account.bank_name} onChange={(e) => updateBankAccount(index, 'bank_name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Branch *</label>
                  <input type="text" value={account.branch} onChange={(e) => updateBankAccount(index, 'branch', e.target.value)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Account Number *</label>
                  <input type="text" value={account.account_number} onChange={(e) => updateBankAccount(index, 'account_number', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Account Type *</label>
                  <select value={account.account_type} onChange={(e) => updateBankAccount(index, 'account_type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Savings">Savings</option>
                    <option value="Current">Current</option>
                    <option value="Fixed Deposit">Fixed Deposit</option>
                  </select>
                </div>
              </div>
              
              {/* Joint Holders Toggle */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}>
                  <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Has Joint Holders?</span>
                  <div 
                    onClick={() => toggleBankJointHolders(index, !account.has_joint_holders)}
                    style={{
                      position: 'relative',
                      width: '52px',
                      height: '28px',
                      background: account.has_joint_holders ? '#f59e0b' : '#475569',
                      borderRadius: '14px',
                      transition: 'background 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: account.has_joint_holders ? '26px' : '2px',
                      width: '24px',
                      height: '24px',
                      background: '#334155',
                      borderRadius: '50%',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                    {account.has_joint_holders ? 'Yes' : 'No'}
                  </span>
                </label>
              </div>

              {/* Joint Holders Section */}
              {account.has_joint_holders && (
                <div style={{ background: '#334155', padding: '1rem', borderRadius: '6px', marginTop: '1rem', border: '1px dashed #475569' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600 }}>Additional Holders</h4>
                  {(account.joint_holders || []).map((holder, holderIndex) => (
                    <div key={holderIndex} style={{ background: '#1e293b', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input 
                            type="text" 
                            value={holder.name || ''} 
                            onChange={(e) => updateJointHolder('financial_assets', index, holderIndex, 'name', e.target.value, 'bank_accounts')}
                            list="names-list"
                            placeholder="Start typing to see suggestions..."
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Relationship *</label>
                          <select 
                            value={holder.relationship || ''} 
                            onChange={(e) => updateJointHolder('financial_assets', index, holderIndex, 'relationship', e.target.value, 'bank_accounts')}
                            required
                          >
                            <option value="">Select Relationship</option>
                            {relationshipOptions.map(rel => (
                              <option key={rel} value={rel}>{rel}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <button 
                          type="button" 
                          onClick={() => removeJointHolder('financial_assets', index, holderIndex, 'bank_accounts')}
                          style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => addJointHolder('financial_assets', index, 'bank_accounts')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#334155', border: '1px solid #475569', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', color: '#f1f5f9' }}
                  >
                    <Plus size={16} /> Add Joint Holder
                  </button>
                </div>
              )}
              <button type="button" onClick={() => removeBankAccount(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Trash2 size={16} /> Remove Account
              </button>
            </div>
          ))}
          <button type="button" onClick={addBankAccount} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Account
          </button>
        </div>
      )}

      {/* Insurance Policies */}
      {currentCategory === 'insurance' && (
        <div className="form-section">
          {formData.financial_assets.insurance.map((ins, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Insurance Company *</label>
                  <input type="text" value={ins.company_name} onChange={(e) => updateInsurance(index, 'company_name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Policy Type *</label>
                  <select value={ins.policy_type} onChange={(e) => updateInsurance(index, 'policy_type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Life Insurance">Life Insurance</option>
                    <option value="Health Insurance">Health Insurance</option>
                    <option value="Vehicle Insurance">Vehicle Insurance</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Policy Number *</label>
                  <input type="text" value={ins.policy_number} onChange={(e) => updateInsurance(index, 'policy_number', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Sum Assured (₹)</label>
                  <input type="number" value={ins.sum_assured} onChange={(e) => updateInsurance(index, 'sum_assured', e.target.value)} />
                </div>
              </div>
              <button type="button" onClick={() => removeInsurance(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Insurance
              </button>
            </div>
          ))}
          <button type="button" onClick={addInsurance} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Policy
          </button>
        </div>
      )}

      {/* Mutual Funds */}
      {currentCategory === 'mutual_funds' && (
        <div className="form-section">
          {/* Mutual Fund Holding Declaration Clauses - Optional */}
          <div style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem', border: '2px solid #475569' }}>
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #334155' }}>
              <h3 style={{ fontWeight: 600, color: '#f1f5f9', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                Mutual Fund Holding Declarations
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                Select the clauses that apply to your mutual fund holdings (optional). These declarations will be included in your will.
              </p>
            </div>

            {/* Clause A: Sole Holder */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', cursor: 'pointer', padding: '1rem', background: formData.mf_clauses?.sole_holder ? 'rgba(245, 158, 11, 0.1)' : '#334155', borderRadius: '8px', marginBottom: '0.75rem', border: formData.mf_clauses?.sole_holder ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid #475569', transition: 'all 0.2s ease' }}>
              <input
                type="checkbox"
                checked={formData.mf_clauses?.sole_holder || false}
                onChange={(e) => updateMFClause('sole_holder', e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#f59e0b', flexShrink: 0 }}
                data-testid="mf-clause-sole-holder"
              />
              <div>
                <span style={{ fontWeight: 600, color: '#f1f5f9', display: 'block', marginBottom: '0.5rem' }}>
                  Sole Holder
                </span>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0, lineHeight: '1.6', textAlign: 'justify' }}>
                  I hold certain mutual fund investments solely in my name, without any joint holder. These include all mutual fund units held by me at the time of my demise, whether existing now or acquired in the future, together with any reinvestments, switches, dividends, or other changes made from time to time. Such investments form part of my estate and shall be governed by this Will.
                </p>
              </div>
            </label>

            {/* Clause B: Primary/First Holder */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', cursor: 'pointer', padding: '1rem', background: formData.mf_clauses?.primary_holder ? 'rgba(245, 158, 11, 0.1)' : '#334155', borderRadius: '8px', marginBottom: '0.75rem', border: formData.mf_clauses?.primary_holder ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid #475569', transition: 'all 0.2s ease' }}>
              <input
                type="checkbox"
                checked={formData.mf_clauses?.primary_holder || false}
                onChange={(e) => updateMFClause('primary_holder', e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#f59e0b', flexShrink: 0 }}
                data-testid="mf-clause-primary-holder"
              />
              <div>
                <span style={{ fontWeight: 600, color: '#f1f5f9', display: 'block', marginBottom: '0.5rem' }}>
                  Primary / First Holder
                </span>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0, lineHeight: '1.6', textAlign: 'justify' }}>
                  I hold certain mutual fund investments as the first or primary holder, whether alone or jointly with another person. These include all such investments existing at the time of my demise or acquired thereafter, together with any reinvestments, switches, dividends, or other changes. Such investments shall be subject to the applicable mode of holding, nomination, and survivorship rules governing mutual funds in India.
                </p>
              </div>
            </label>

            {/* Clause C: Secondary/Joint Holder */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', cursor: 'pointer', padding: '1rem', background: formData.mf_clauses?.secondary_holder ? 'rgba(245, 158, 11, 0.1)' : '#334155', borderRadius: '8px', border: formData.mf_clauses?.secondary_holder ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid #475569', transition: 'all 0.2s ease' }}>
              <input
                type="checkbox"
                checked={formData.mf_clauses?.secondary_holder || false}
                onChange={(e) => updateMFClause('secondary_holder', e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#f59e0b', flexShrink: 0 }}
                data-testid="mf-clause-secondary-holder"
              />
              <div>
                <span style={{ fontWeight: 600, color: '#f1f5f9', display: 'block', marginBottom: '0.5rem' }}>
                  Secondary / Joint Holder
                </span>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0, lineHeight: '1.6', textAlign: 'justify' }}>
                  I hold certain mutual fund investments as a secondary or joint holder and not as the primary holder. I acknowledge that such investments may pass to the surviving primary holder in accordance with the applicable holding terms and transmission rules. Only any beneficial interest that legally forms part of my estate shall be governed by this Will.
                </p>
              </div>
            </label>
          </div>

          {/* Info message */}
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.85rem', color: '#f59e0b', margin: 0 }}>
              💡 The above declarations cover your mutual fund holdings. You may select none, one, or multiple clauses based on your holding patterns.
            </p>
          </div>
        </div>
      )}

      {/* Shares & Securities */}
      {currentCategory === 'shares' && (
        <div className="form-section">
          {/* Tooltip message for Shares */}
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.85rem', color: '#f59e0b', margin: 0 }}>
              💡 Try to ensure Nominees are the same as Beneficiaries
            </p>
          </div>
          {formData.financial_assets.shares.map((share, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #334155' }}>
                <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Demat Account {index + 1}</span>
              </div>

              {/* Manual Entry Section */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: '0.9rem' }}>Demat Account Details</span>
                </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Broker Name *</label>
                      <input type="text" value={share.company_name} onChange={(e) => updateShare(index, 'company_name', e.target.value)} placeholder="e.g., Zerodha, ICICI Direct" required />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        DEMAT Account Number (BO ID)
                        <span 
                          title="A Demat ID (or Beneficiary Owner ID/BO ID) is a 16-digit unique identifier for your electronic share holdings. CDSL IDs are entirely numeric (16 digits), while NSDL IDs start with &quot;IN&quot; followed by 14 digits. The first 8 digits represent the DP ID (Broker), and the last 8 are the client ID."
                          style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', background: '#475569', color: '#f1f5f9', fontSize: '0.7rem', fontWeight: 700 }}
                        >?</span>
                      </label>
                      <input type="text" value={share.demat_account} onChange={(e) => updateShare(index, 'demat_account', e.target.value)} placeholder="16-digit CDSL/NSDL ID" />
                    </div>
                  </div>
              </div>
              
              {/* Joint Holders Toggle */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}>
                  <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Has Joint Holders?</span>
                  <div 
                    onClick={() => toggleShareJointHolders(index, !share.has_joint_holders)}
                    style={{
                      position: 'relative',
                      width: '52px',
                      height: '28px',
                      background: share.has_joint_holders ? '#f59e0b' : '#475569',
                      borderRadius: '14px',
                      transition: 'background 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: share.has_joint_holders ? '26px' : '2px',
                      width: '24px',
                      height: '24px',
                      background: '#334155',
                      borderRadius: '50%',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                    {share.has_joint_holders ? 'Yes' : 'No'}
                  </span>
                </label>
              </div>

              {/* Joint Holders Section */}
              {share.has_joint_holders && (
                <div style={{ background: '#334155', padding: '1rem', borderRadius: '6px', marginTop: '1rem', border: '1px dashed #475569' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600 }}>Additional Holders</h4>
                  {(share.joint_holders || []).map((holder, holderIndex) => (
                    <div key={holderIndex} style={{ background: '#1e293b', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input 
                            type="text" 
                            value={holder.name || ''} 
                            onChange={(e) => updateJointHolder('financial_assets', index, holderIndex, 'name', e.target.value, 'shares')}
                            list="names-list"
                            placeholder="Start typing to see suggestions..."
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Relationship *</label>
                          <select 
                            value={holder.relationship || ''} 
                            onChange={(e) => updateJointHolder('financial_assets', index, holderIndex, 'relationship', e.target.value, 'shares')}
                            required
                          >
                            <option value="">Select Relationship</option>
                            {relationshipOptions.map(rel => (
                              <option key={rel} value={rel}>{rel}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button 
                            type="button" 
                            onClick={() => removeJointHolder('financial_assets', index, holderIndex, 'shares')}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                          >
                            <Trash2 size={16} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => addJointHolder('financial_assets', index, 'shares')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#334155', border: '1px solid #475569', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', color: '#f1f5f9' }}
                  >
                    <Plus size={16} /> Add Joint Holder
                  </button>
                </div>
              )}
              
              <button type="button" onClick={() => removeShare(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Shares
              </button>
            </div>
          ))}
          <button type="button" onClick={addShare} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add More Shares/Securities
          </button>
        </div>
      )}

      {/* PF & Pension */}
      {currentCategory === 'pf_pension' && (
        <div className="form-section">
          {formData.financial_assets.pf_pension.map((pf, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select value={pf.type} onChange={(e) => updatePFPension(index, 'type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="EPF">EPF (Employees' Provident Fund)</option>
                    <option value="PPF">PPF (Public Provident Fund)</option>
                    <option value="NPS">NPS (National Pension System)</option>
                    <option value="Pension">Pension</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Account Number *</label>
                  <input type="text" value={pf.account_number} onChange={(e) => updatePFPension(index, 'account_number', e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Organization / Bank</label>
                <input type="text" value={pf.organization} onChange={(e) => updatePFPension(index, 'organization', e.target.value)} placeholder="e.g., Previous employer name" />
              </div>
              <button type="button" onClick={() => removePFPension(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove PF/Pension
              </button>
            </div>
          ))}
          <button type="button" onClick={addPFPension} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another PF/Pension
          </button>
        </div>
      )}

      {/* Jewellery & Valuables */}
      {currentCategory === 'jewellery' && (
        <div className="form-section">
          {formData.tangible_assets.filter(a => a.type === 'jewellery' || a.type === 'valuable' || a.type === 'gold' || a.type === 'silver' || a.type === 'artwork').map((item, index) => {
            const actualIndex = formData.tangible_assets.findIndex(a => a === item);
            return (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #ef4444' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select value={item.type} onChange={(e) => updateValuable(index, 'type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="jewellery">Jewellery (Gold, Diamond, etc.)</option>
                    <option value="gold">Metals - Gold</option>
                    <option value="silver">Metals - Silver</option>
                    <option value="artwork">Artwork / Antique</option>
                    <option value="valuable">Other Valuable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <input 
                    type="text" 
                    value={item.description} 
                    onChange={(e) => updateValuable(index, 'description', e.target.value)} 
                    placeholder={
                      item.type === 'gold' ? 'e.g., Gold bars, coins' : 
                      item.type === 'silver' ? 'e.g., Silver bars, coins' :
                      item.type === 'jewellery' ? 'e.g., Gold necklace, Diamond ring' :
                      item.type === 'artwork' ? 'e.g., Painting by artist, Antique furniture' :
                      'Describe the item'
                    }
                    required 
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  {item.type === 'jewellery' || item.type === 'gold' || item.type === 'silver' ? 
                    'Approx Gms' : 
                    'Approx. Value (₹)'}
                </label>
                <input 
                  type="number" 
                  value={item.value} 
                  onChange={(e) => updateValuable(index, 'value', e.target.value)}
                  placeholder={item.type === 'jewellery' || item.type === 'gold' || item.type === 'silver' ? 'Enter weight in grams' : 'Enter value in rupees'}
                  step={item.type === 'jewellery' || item.type === 'gold' || item.type === 'silver' ? '0.01' : '1'}
                />
              </div>
              <div className="form-group">
                <label>Additional Notes</label>
                <textarea 
                  value={item.additional_notes || ''} 
                  onChange={(e) => updateValuable(index, 'additional_notes', e.target.value)} 
                  rows="2"
                  placeholder="Any additional details about this item"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #475569', borderRadius: '6px', fontFamily: 'inherit' }}
                ></textarea>
              </div>
              <button type="button" onClick={() => removeValuable(actualIndex)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Item
              </button>
            </div>
          );
          })}
          <button type="button" onClick={addValuable} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Item
          </button>
        </div>
      )}

      {/* Safety Lockers */}
      {currentCategory === 'locker' && (
        <div className="form-section">
          {formData.safety_lockers.map((locker, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name *</label>
                  <input type="text" value={locker.bank_name} onChange={(e) => updateLocker(index, 'bank_name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Branch</label>
                  <input type="text" value={locker.branch} onChange={(e) => updateLocker(index, 'branch', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Locker Number *</label>
                  <input type="text" value={locker.locker_number} onChange={(e) => updateLocker(index, 'locker_number', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Key Location</label>
                  <input type="text" value={locker.key_location} onChange={(e) => updateLocker(index, 'key_location', e.target.value)} placeholder="Where is the key stored?" />
                </div>
              </div>
              <button type="button" onClick={() => removeLocker(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Locker
              </button>
            </div>
          ))}
          <button type="button" onClick={addLocker} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Locker
          </button>
        </div>
      )}

      {/* Business Interests */}
      {currentCategory === 'business' && (
        <div className="form-section">
          {formData.business_interests.map((business, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Business Type *</label>
                  <select value={business.business_type} onChange={(e) => updateBusiness(index, 'business_type', e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Proprietary">Proprietary</option>
                    <option value="LLP">LLP (Limited Liability Partnership)</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Pvt Ltd">Pvt Ltd (Private Limited)</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                {business.business_type === 'Others' && (
                  <div className="form-group">
                    <label>Specify Business Type *</label>
                    <input type="text" value={business.other_type} onChange={(e) => updateBusiness(index, 'other_type', e.target.value)} placeholder="Enter business type" required />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Business Name *</label>
                <input type="text" value={business.business_name} onChange={(e) => updateBusiness(index, 'business_name', e.target.value)} placeholder="Company/Firm name" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Shares Held</label>
                  <input type="number" value={business.shares_held} onChange={(e) => updateBusiness(index, 'shares_held', e.target.value)} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>% of Firm Held *</label>
                  <input type="number" value={business.ownership_percentage} onChange={(e) => updateBusiness(index, 'ownership_percentage', e.target.value)} placeholder="0" min="0" max="100" step="0.01" required />
                </div>
              </div>
              
              {/* Joint Holders Toggle */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}>
                  <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Has Joint Holders?</span>
                  <div 
                    onClick={() => updateBusiness(index, 'has_joint_holders', !business.has_joint_holders)}
                    style={{
                      position: 'relative',
                      width: '52px',
                      height: '28px',
                      background: business.has_joint_holders ? '#f59e0b' : '#475569',
                      borderRadius: '14px',
                      transition: 'background 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: business.has_joint_holders ? '26px' : '2px',
                      width: '24px',
                      height: '24px',
                      background: '#334155',
                      borderRadius: '50%',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                    {business.has_joint_holders ? 'Yes' : 'No'}
                  </span>
                </label>
              </div>

              {/* Joint Holders Section */}
              {business.has_joint_holders && (
                <div style={{ background: '#334155', padding: '1rem', borderRadius: '6px', marginTop: '1rem', border: '1px dashed #475569' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600 }}>Additional Holders</h4>
                  {(business.joint_holders || []).map((holder, holderIndex) => (
                    <div key={holderIndex} style={{ background: '#1e293b', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input 
                            type="text" 
                            value={holder.name || ''} 
                            onChange={(e) => updateJointHolder('business_interests', index, holderIndex, 'name', e.target.value)}
                            list="names-list"
                            placeholder="Start typing to see suggestions..."
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Relationship *</label>
                          <select 
                            value={holder.relationship || ''} 
                            onChange={(e) => updateJointHolder('business_interests', index, holderIndex, 'relationship', e.target.value)}
                            required
                          >
                            <option value="">Select Relationship</option>
                            {relationshipOptions.map(rel => (
                              <option key={rel} value={rel}>{rel}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Share % (Optional)</label>
                          <input 
                            type="number" 
                            value={holder.share_percentage || ''} 
                            onChange={(e) => updateJointHolder('business_interests', index, holderIndex, 'share_percentage', e.target.value)}
                            placeholder="0"
                            min="0"
                            max="100" step="0.01"
                          />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button 
                            type="button" 
                            onClick={() => removeJointHolder('business_interests', index, holderIndex)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                          >
                            <Trash2 size={16} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => addJointHolder('business_interests', index)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#334155', border: '1px solid #475569', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', color: '#f1f5f9' }}
                  >
                    <Plus size={16} /> Add Joint Holder
                  </button>
                </div>
              )}
              
              <button type="button" onClick={() => removeBusiness(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Business Interest
              </button>
            </div>
          ))}
          <button type="button" onClick={addBusiness} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Business
          </button>
        </div>
      )}

      {/* ESOPs / RSUs */}
      {currentCategory === 'esop_rsu' && (
        <div className="form-section">
          {formData.esops.map((esop, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #f59e0b' }}>
              <div className="form-group">
                <label>Firm Name *</label>
                <input type="text" value={esop.firm_name} onChange={(e) => updateEsop(index, 'firm_name', e.target.value)} placeholder="Company name" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Total Shares Held *</label>
                  <input type="number" value={esop.total_shares} onChange={(e) => updateEsop(index, 'total_shares', e.target.value)} placeholder="0" required />
                </div>
                <div className="form-group">
                  <label>Vested ESOPs *</label>
                  <input type="number" value={esop.vested_esops} onChange={(e) => updateEsop(index, 'vested_esops', e.target.value)} placeholder="0" required />
                </div>
              </div>
              <div className="form-group">
                <label>Unvested ESOPs *</label>
                <input type="number" value={esop.unvested_esops} onChange={(e) => updateEsop(index, 'unvested_esops', e.target.value)} placeholder="0" required />
              </div>
              <button type="button" onClick={() => removeEsop(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove ESOP/RSU
              </button>
            </div>
          ))}
          <button type="button" onClick={addEsop} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another ESOP/RSU
          </button>
        </div>
      )}

      {/* Digital Assets */}
      {currentCategory === 'digital_assets' && (
        <div className="form-section">
          {formData.digital_assets.map((asset, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Platform Name *</label>
                  <input type="text" value={asset.platform_name} onChange={(e) => updateDigitalAsset(index, 'platform_name', e.target.value)} placeholder="e.g., Coinbase, Binance, OpenSea" required />
                </div>
                <div className="form-group">
                  <label>Wallet Address *</label>
                  <input type="text" value={asset.wallet_address} onChange={(e) => updateDigitalAsset(index, 'wallet_address', e.target.value)} placeholder="Wallet address or account ID" required />
                </div>
              </div>
              <div className="form-group">
                <label>Asset Description</label>
                <input type="text" value={asset.description} onChange={(e) => updateDigitalAsset(index, 'description', e.target.value)} placeholder="e.g., Bitcoin, Ethereum, Domain name" />
              </div>
              <button type="button" onClick={() => removeDigitalAsset(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Digital Asset
              </button>
            </div>
          ))}
          <button type="button" onClick={addDigitalAsset} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Digital Asset
          </button>
        </div>
      )}

      {/* Intellectual Property */}
      {currentCategory === 'intellectual_property' && (
        <div className="form-section">
          {formData.intellectual_property.map((ip, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>IP Name *</label>
                  <input type="text" value={ip.name} onChange={(e) => updateIP(index, 'name', e.target.value)} placeholder="Patent, Trademark, or Copyright name" required />
                </div>
                <div className="form-group">
                  <label>Unique Identifier *</label>
                  <input type="text" value={ip.unique_identifier} onChange={(e) => updateIP(index, 'unique_identifier', e.target.value)} placeholder="Registration number" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Location / Registry</label>
                  <input type="text" value={ip.location} onChange={(e) => updateIP(index, 'location', e.target.value)} placeholder="e.g., Indian Patent Office" />
                </div>
                <div className="form-group">
                  <label>Patent No. / Registration No.</label>
                  <input type="text" value={ip.patent_no} onChange={(e) => updateIP(index, 'patent_no', e.target.value)} placeholder="Official registration number" />
                </div>
              </div>
              <button type="button" onClick={() => removeIP(index)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <Trash2 size={16} /> Remove Intellectual Property
              </button>
            </div>
          ))}
          <button type="button" onClick={addIP} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another IP
          </button>
        </div>
      )}

      {/* Foreign Assets */}
      {currentCategory === 'foreign_assets' && (
        <div className="form-section">
          {/* Tooltip Info Box */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)', 
            border: '1px solid #f59e0b50', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <div style={{ 
              background: '#f59e0b', 
              borderRadius: '50%', 
              width: '24px', 
              height: '24px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{ color: '#0f172a', fontWeight: 'bold', fontSize: '14px' }}>i</span>
            </div>
            <p style={{ color: '#f59e0b', fontSize: '0.875rem', margin: 0, lineHeight: '1.5' }}>
              <strong>Tip:</strong> Give as much detail as possible to avoid any ambiguity, even for someone who has not seen the asset. Mention Country, Institution, Account Numbers, Type of Asset.
            </p>
          </div>

          {formData.foreign_assets.map((asset, index) => (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #475569' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ color: '#f59e0b', margin: 0 }}>Foreign Asset #{index + 1}</h4>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country *</label>
                  <input 
                    type="text" 
                    value={asset.country} 
                    onChange={(e) => updateForeignAsset(index, 'country', e.target.value)} 
                    placeholder="e.g., United States, United Kingdom, Singapore" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Institution / Bank Name *</label>
                  <input 
                    type="text" 
                    value={asset.institution} 
                    onChange={(e) => updateForeignAsset(index, 'institution', e.target.value)} 
                    placeholder="e.g., HSBC Bank, Charles Schwab" 
                    required 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Account Number / Reference</label>
                  <input 
                    type="text" 
                    value={asset.account_number} 
                    onChange={(e) => updateForeignAsset(index, 'account_number', e.target.value)} 
                    placeholder="Account number or unique reference" 
                  />
                </div>
                <div className="form-group">
                  <label>Type of Asset *</label>
                  <select 
                    value={asset.asset_type} 
                    onChange={(e) => updateForeignAsset(index, 'asset_type', e.target.value)}
                    required
                  >
                    <option value="">Select type</option>
                    <option value="Bank Account">Bank Account</option>
                    <option value="Investment Account">Investment Account</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Retirement Account">Retirement Account (401k, IRA, etc.)</option>
                    <option value="Insurance Policy">Insurance Policy</option>
                    <option value="Business Interest">Business Interest</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Detailed Description *</label>
                <textarea 
                  value={asset.description} 
                  onChange={(e) => updateForeignAsset(index, 'description', e.target.value)} 
                  placeholder="Provide comprehensive details about this asset including addresses, additional reference numbers, joint holders, nominees (if any), approximate value, and any other relevant information that would help identify and transfer this asset."
                  style={{ 
                    background: '#0f172a', 
                    border: '1px solid #475569', 
                    borderRadius: '8px', 
                    padding: '0.75rem', 
                    color: '#f1f5f9', 
                    width: '100%', 
                    minHeight: '120px',
                    resize: 'vertical',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
              <button 
                type="button" 
                onClick={() => removeForeignAsset(index)} 
                style={{ 
                  color: '#ef4444', 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  marginTop: '1rem' 
                }}
              >
                <Trash2 size={16} /> Remove Foreign Asset
              </button>
            </div>
          ))}
          <button 
            type="button" 
            onClick={addForeignAsset} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              padding: '0.75rem 1.5rem', 
              background: '#1e293b', 
              border: '1px solid #475569', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontWeight: 600,
              color: '#f1f5f9'
            }}
          >
            <Plus size={20} /> Add Another Foreign Asset
          </button>
        </div>
      )}

      {/* Vehicles */}
      {currentCategory === 'vehicles' && (
        <div className="form-section">
          {formData.tangible_assets.filter(a => a.type === 'vehicle').map((vehicle, index) => {
            const actualIndex = formData.tangible_assets.findIndex(a => a === vehicle);
            return (
            <div key={index} style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Vehicle Description *</label>
                  <input type="text" value={vehicle.description} onChange={(e) => updateVehicle(index, 'description', e.target.value)} placeholder="e.g., Honda City 2020" required />
                </div>
                <div className="form-group">
                  <label>Registration Number *</label>
                  <input 
                    type="text" 
                    value={vehicle.registration_no} 
                    onChange={(e) => updateVehicle(index, 'registration_no', e.target.value.toUpperCase())} 
                    placeholder="e.g., MH01AB1234"
                    style={{ textTransform: 'uppercase' }}
                    required 
                  />
                </div>
              </div>
              <button type="button" onClick={() => removeVehicle(actualIndex)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Trash2 size={16} /> Remove Vehicle
              </button>
            </div>
          );
          })}
          <button type="button" onClick={addVehicle} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1e293b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={20} /> Add Another Vehicle
          </button>
        </div>
      )}

      {/* Category Navigation at Bottom */}
      <div style={{ marginTop: '2rem', marginBottom: '1rem' }}>
        <CategoryNavigation />
      </div>

      <div className="step-nav">
        <button type="button" className="btn-secondary" onClick={onPrevious}>
          <ChevronLeft size={20} /> Previous
        </button>
        <button type="button" className="btn-save" onClick={() => onSaveAndExit(formData)} disabled={saving}>
          <Save size={20} /> Save & Exit
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          Next: Beneficiaries <ChevronRight size={20} />
        </button>
      </div>
    </form>
  );
};

export default Step4Assets;
